import styled from '@emotion/styled';
import { PaperColorElevation } from '../../components/PaperColorElevation';
import CardActionArea from '@mui/material/CardActionArea';
import { CardAsset, CardSkeleton } from '../../components/Card';
import { Typography as MuiTypography, Button as MuiButton, useMediaQuery, withStyles } from '@mui/material';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import React from 'react';

interface RootProps extends React.HTMLAttributes<HTMLDivElement> {
  isfullscreen?: boolean;
}

interface HeaderSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  isfullscreen?: boolean;
}

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

export const Root = styled('div')<RootProps>(({ theme, isfullscreen }) => ({
  display: 'grid',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  gap: theme.spacing(2),
  gridTemplateColumns: '100%',
  gridTemplateRows: 'auto',
  gridTemplateAreas: `
        'A'
        'D'
        'B'
        'C'
        'F'
        'E'
        'G'`,
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateAreas: `
        'A A'
        'D D'
        'B B'
        'C E'
        'F F'
        'G G'`,
  },
  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(4),
    // gridTemplate: `repeat(12, 1fr)`,
    // gridTemplateColumns: 'repeat(auto-fill, 1fr auto)',
    // gridTemplateRows: `${300 + theme.spacing(1)}px ${300 + theme.spacing(1)}px ${300 + theme.spacing(1)}px 250px 250px`,
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridTemplateAreas: `
      ${isfullscreen ? `
        'A A A A A A A A A A A A'
        'B B B B B B B B B B B B'
        'C C C C C C E E E E E E'
        'F F F F F F F F F F F F'
        'G G G G G G G G G G G G'` : 
        `'A A A A A A A A A A A A'
        'B B B B B B B B B B B B'
        'C C C C D D D D E E E E'
        'F F F F F F F F F F F F'
        'G G G G G G G G G G G G'`
      }
    `,
  },
}));

export const HeaderSection = styled('div')<HeaderSectionProps>(({ theme, isfullscreen }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'auto',
  gridTemplateRows: 'auto',
  gridTemplateAreas: `
        'carousel'
        'skyscraper'
        'editor'
        'releases'`,
  width: '100%',
  padding: theme.spacing(0, 2),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: `auto auto ${isfullscreen ? '0' : 300 + parseInt(theme.spacing(4).replace('px ', ''))}px`,
    gridTemplateAreas: `
        'carousel carousel skyscraper'
        'editor editor skyscraper'
        'releases releases releases'`,
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(0, 2),
    gridTemplateColumns: `minmax(auto, auto) minmax(auto, 575px) ${isfullscreen ? '0' : 300 + parseInt(theme.spacing(4).replace('px ', ''))}px`,
    gridTemplateAreas: `
        'carousel editor skyscraper'
        'releases releases skyscraper'`,
  },
  [theme.breakpoints.up('xl')]: {
    gridTemplateColumns: `minmax(auto, auto) minmax(auto, 575px) ${isfullscreen ? '0' : 300 + parseInt(theme.spacing(4).replace('px ', ''))}px`,
  },
}));

export const EditorsSection = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gridTemplateRows: 'auto',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: `repeat(${theme.theme_component_number_of_popular_games_to_show || 4}, minmax(0, 1fr))`,
  },
}));


export const FeaturedListSection = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gridTemplateRows: '1fr',
  width: '100%',
  padding: theme.spacing(2, 0, 0),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  },
}));

export const MiddleSectionLeft = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gridTemplateRows: 'auto',
  padding: theme.spacing(2),
}));

export const MiddleSectionRight = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gridTemplateRows: 'auto',
  padding: theme.spacing(2),
}));

export const Box = styled(PaperColorElevation)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[0],
}));

export const Box1 = styled(Box)(() => ({
  gridArea: 'A',
  borderRadius: 0,
  // backgroundColor: '#383838',

  // Very important, if left out, SwiperJS will break.
  // https://github.com/nolimits4web/swiper/issues/2914#issuecomment-787610135
  // overflow: 'hidden',
}));

export const Box2 = styled(Box)(({ theme }) => ({
  gridArea: 'B',
  borderRadius: 0,
  marginTop: theme.spacing(-1.25), // Considering title top padding.
  // backgroundColor: '#e74c3c',

  // Very important, if left out, SwiperJS will break.
  // https://github.com/nolimits4web/swiper/issues/2914#issuecomment-787610135
  overflow: 'hidden',
}));

export const Box3 = styled(Box)(({ theme }) => ({
  gridArea: 'C',
  justifyContent: 'normal',
  alignItems: 'normal',
  flexDirection: 'column',
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  // backgroundColor: '#16a085',
  [theme.breakpoints.up('md')]: {
    marginRight: 0,
  },
}));

export const Box4 = styled(Box)(({ theme }) => ({
  gridArea: 'D',
  // backgroundColor: '#2ecc71',
  // margin: theme.spacing(0, 2),
  margin: 0,
  borderRadius: 0,
  padding: theme.spacing(2, 0),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
    margin: theme.spacing(0, 2),
    borderRadius: theme.shape.borderRadius,
  },
  [theme.breakpoints.up('md')]: {
    margin: 0,
  },
}));

export const Box5 = styled(Box)(({ theme }) => ({
  gridArea: 'E',
  justifyContent: 'normal',
  alignItems: 'normal',
  flexDirection: 'column',
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  // backgroundColor: '#3298db',
  [theme.breakpoints.up('md')]: {
    marginLeft: 0,
  },
}));

export const Box6 = styled(Box)(({ theme }) => ({
  gridArea: 'F',
  borderRadius: 0,
  marginTop: theme.spacing(-1.25), // Considering title top padding.
  marginBottom: theme.spacing(-1.25), // Considering title top padding.
  // backgroundColor: '#9b59b6',

  // Very important, if left out, SwiperJS will break.
  // https://github.com/nolimits4web/swiper/issues/2914#issuecomment-787610135
  overflow: 'hidden',
}));

export const Box7 = styled(Box)(({ theme }) => ({
  gridArea: 'G',
  // backgroundColor: '#3298db',
  padding: theme.spacing(2),
  borderRadius: 0,
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(2, 0),
  },
}));

export const Box1a = styled(Box)(({ theme }) => ({
  gridArea: 'carousel',

  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.grey['900'],

  [theme.breakpoints.up('lg')]: {
    maxHeight: 480, // Same as box2a
  },
  [theme.breakpoints.down('xl')]: {
    '& > div:first-of-type': {
      aspectRatio: '16/9',
      marginTop: 'auto',
      marginBottom: 'auto',
    },
    '& > div:last-of-type': {
      maxHeight: '20%',
    },
  },
  [theme.breakpoints.up('xl')]: {
    flexDirection: 'row',
    '& > div:last-of-type': {
      maxWidth: '20%',
      maxHeight: '100%',
      height: '100%',
    },
  },
  // backgroundColor: '#2ecc71',
  // [theme.breakpoints.only('lg')]: {
  //   padding: theme.spacing(2),
  // },

  // Very important, if left out, SwiperJS will break.
  // https://github.com/nolimits4web/swiper/issues/2914#issuecomment-787610135
  overflow: 'hidden',
}));

export const Box2a = styled(Box)(({ theme }) => ({
  gridArea: 'editor',
  justifyContent: 'normal',
  alignItems: 'normal',
  flexDirection: 'column',
  [theme.breakpoints.up('lg')]: {
    maxHeight: 480, // Same as box1a grid
  },
  // backgroundColor: '#3298db',
}));

export const Box3a = styled(Box)(({ theme }) => ({
  gridArea: 'skyscraper',
  // backgroundColor: '#9b59b6',
  padding: theme.spacing(2),
  maxHeight: `calc(600px + ${theme.spacing(4)})`,
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(2, 0),
    margin: theme.spacing(0, -2),
    borderRadius: 0,
  },
}));

export const Box4a = styled(Box)(({ theme }) => ({
  gridArea: 'releases',
  justifyContent: 'flex-end',
  alignItems: 'normal',
  flexDirection: 'column',
  background: 'none',
  marginTop: theme.spacing(-1.25), // Considering title top padding.
  // backgroundColor: '#e67e22',
}));

export const CardPaperColorElevationFlat = styled(PaperColorElevation)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  overflow: 'hidden',
})) as typeof PaperColorElevation;

export const CardActionAreaFlat = styled(CardActionArea)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})) as typeof CardActionArea;

export const CardAssetFlat = styled(CardAsset)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: 0,
  overflow: 'hidden',
})) as typeof CardAsset;

export const CardContentFlat = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  maxWidth: `calc(100% - ${theme.spacing(4)})`,
}));

export const CardTypographyFlat = styled(Typography)(() => ({
  width: '100%',
  height: 'fit-content',
  display: 'inline-block',
}));

export const CardSkeletonStyled = styled(CardSkeleton)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  aspectRatio: '16/9',
}));

export const CardSkeletonGrid = styled(CardSkeleton)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  aspectRatio: '4/3',
}));